/**
 * Commission rates are fractions in [0, 1], stored as `Numeric(5, 4)` in PostgreSQL
 * (4 fractional digits on the rate, e.g. 0.1564 = 15.64%).
 *
 * UI edits a **percent** in [0, 100] with at most **2** fractional digits (so the
 * implied rate never needs more than 4 decimals). Parsing/display uses snapping so
 * IEEE-754 noise does not leak into inputs or labels.
 */

/** Matches backend `Numeric(5, 4)` for rates in [0, 1]. */
export const COMMISSION_RATE_DECIMAL_PLACES = 4;

const RATE_SCALE = 10 ** COMMISSION_RATE_DECIMAL_PLACES;

export function snapCommissionRate(rate: number): number {
  if (!Number.isFinite(rate)) return 0;
  const clamped = Math.min(1, Math.max(0, rate));
  return Math.round(clamped * RATE_SCALE) / RATE_SCALE;
}

/** Percent in [0, 100] snapped to 2 dp, then converted to a snapped rate. */
export function rateFromUIPercent(percent: number): number {
  if (!Number.isFinite(percent)) return 0;
  const p = Math.min(100, Math.max(0, percent));
  const percent2dp = Math.round(p * 100) / 100;
  return snapCommissionRate(percent2dp / 100);
}

/**
 * Percent string for labels / inputs (no "%"), max 2 fractional digits,
 * stable for a snapped rate.
 */
export function commissionPercentStringFromRate(rate: number): string {
  const r = snapCommissionRate(rate);
  const hundredths = Math.round(r * 100 * 100);
  const whole = Math.floor(hundredths / 100);
  const frac2 = hundredths % 100;
  if (frac2 === 0) return String(whole);
  return `${whole}.${frac2.toString().padStart(2, "0")}`;
}

/** `"15,64"` / `"15.64"` → snapped rate, or `null` if invalid. */
export function rateFromPercentInput(raw: string): number | null {
  const n = Number.parseFloat(raw.trim().replace(",", "."));
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return rateFromUIPercent(n);
}

export function formatCommissionPercentLabel(rate: number): string {
  return `${commissionPercentStringFromRate(rate)}%`;
}

/** Partner share shown in the portal (100% minus platform commission stored on the activity). */
export function partnerShareRateFromPlatformRate(platformRate: number): number {
  return snapCommissionRate(1 - snapCommissionRate(platformRate));
}
